import { EventEmitter, Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { removeBlank, removeNullish } from 'src/app/core/helpers';
import { AbilityService } from 'src/app/core/services/ability/ability.service';
import { ApiService } from 'src/app/core/services/api.service';
import { localRoleOptions, UserRoleOption } from './constants';

@Injectable({ providedIn: 'root' })
export class UserManagementService extends ApiService {
  public eventEmitter = new EventEmitter();
  private readonly ability = inject(AbilityService);

  /**
   * The roles this user may see and assign. It comes from the server so that
   * which roles are visible, and which of those can be handed out, is decided
   * in the same place that enforces it on save — a client-side copy drifts and
   * then offers a choice the API refuses.
   *
   * A failed request falls back to the bundled matrix rather than leaving the
   * Role dropdown empty.
   */
  public getRoleOptions(): Observable<UserRoleOption[]> {
    return this.request({ path: 'users/roles', method: 'GET' }).pipe(
      map((roles: any[]) =>
        (roles || []).map((role) => ({
          value: role.value,
          viewValue: role.label,
          canAssign: !!role.canAssign,
        })),
      ),
      catchError(() => of(localRoleOptions(this.ability.role))),
    );
  }
  public create(data): Observable<any> {
    return this.request({
      path: 'users',
      method: 'POST',
      body: data,
    });
  }
  public changeStatus(data, id): Observable<any> {
    return this.request({
      path: `users/${id}/access`,
      method: 'POST',
      body: data,
    });
  }
  public getUserList(params): Observable<any> {
    return this.request({
      path: 'users',
      method: 'GET',
      query: removeNullish(params),
    });
  }
  public getUserById(id): Observable<any> {
    return this.request({
      path: `users/${id}`,
      method: 'GET',
    });
  }
  public updateUser(data, id): Observable<any> {
    return this.request({
      path: `users/${id}`,
      method: 'PUT',
      body: data,
    });
  }

  public updateUserPassword(data, id): Observable<any> {
    return this.request({
      path: `users/${id}/update-user-password`,
      method: 'PATCH',
      body: data,
    });
  }

  /**
   * Mints a reset link for the given user and returns it instead of emailing
   * it, so an admin can pass it on themselves.
   */
  public generatePasswordResetLink(
    id
  ): Observable<{ link: string; expiresAt: string }> {
    return this.request({
      path: `users/${id}/password-reset-link`,
      method: 'POST',
    });
  }

  /**
   * Whether this lab may change the user's password, asked before the reset
   * dialog offers a form. Under SSO one password serves every lab the account
   * belongs to, so a shared account is not this lab's to change.
   */
  public passwordResetEligibility(
    id
  ): Observable<{ canReset: boolean; labCount: number }> {
    return this.request({
      path: `users/${id}/password-reset-eligibility`,
      method: 'GET',
    });
  }

  /** Record and email a request for help with an account. */
  public contactSupport(id, message: string): Observable<{ message: string }> {
    return this.request({
      path: `users/${id}/contact-support`,
      method: 'POST',
      body: { message },
    });
  }

  public deleteUser(id): Observable<any> {
    return this.request({
      path: `users/${id}`,
      method: 'DELETE',
    });
  }
}
